import { useRef, useState } from 'react';
import { exportAllData, importAllData } from './storage';
import { DEFAULT_PREFS } from './useNotifications';
import './Settings.css';

/* ── Notification type definitions ── */
const NOTIF_TYPES = [
  {
    key:  'red',
    icon: '🔴',
    label: 'RED Alert',
    desc:  'Cushion below 5 % — stop-loss may trigger soon',
  },
  {
    key:  'amber',
    icon: '🟡',
    label: 'AMBER Alert',
    desc:  'Cushion 5–15 % — approaching stop-loss level',
  },
  {
    key:  'cryptoPct',
    icon: '₿',
    label: 'Crypto Overweight',
    desc:  'Crypto exceeds 15 % of total portfolio (NLV)',
  },
  {
    key:  'leverage',
    icon: '⚖️',
    label: 'Leverage Ratio',
    desc:  'TQQQ + UPRO outside 75 – 85 % target band',
  },
  {
    key:  'weekly',
    icon: '📋',
    label: 'Weekly Review',
    desc:  'Sunday 9 am reminder (app must be open on iPhone)',
  },
];

export default function Settings({ notifProps }) {
  /* Destructure with safe defaults so Settings renders fine even without notifProps */
  const {
    permission        = 'unsupported',
    prefs             = DEFAULT_PREFS,
    setPrefs          = () => {},
    requestPermission = async () => {},
  } = notifProps ?? {};
  const fileRef = useRef(null);
  const [importState, setImportState] = useState(null); // null | 'confirm' | 'success' | {error}
  const [pendingData, setPendingData] = useState(null);

  /* ── Export ── */
  function handleExport() {
    const data = exportAllData();
    const date = new Date().toISOString().slice(0, 10);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `investment-backup-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ── Import: file selected ── */
  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const parsed = JSON.parse(evt.target.result);
        if (!parsed.version) throw new Error('Missing version field — not a valid backup.');
        setPendingData(parsed);
        setImportState('confirm');
      } catch (err) {
        setImportState({ error: err.message });
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // reset so same file can be re-selected
  }

  /* ── Import: confirmed ── */
  function handleImportConfirm() {
    try {
      importAllData(pendingData);
      setImportState('success');
      setPendingData(null);
    } catch (err) {
      setImportState({ error: err.message });
    }
  }

  function handleImportCancel() {
    setImportState(null);
    setPendingData(null);
  }

  /* ── Export summary ── */
  const summary = (() => {
    try {
      const d = exportAllData();
      return [
        { label: 'Positions',         value: d.positions?.length  ?? 0 },
        { label: 'Watchlist stocks',  value: d.fmStocks?.length   ?? 0 },
        { label: 'Weekly reviews',    value: d.reviews?.length    ?? 0 },
        { label: 'ETF / DCA inputs',  value: Object.keys(d.dcaInputs ?? {}).length > 0 ? 'saved' : 'none' },
      ];
    } catch { return []; }
  })();

  return (
    <div className="settings-screen">
      <h2 className="settings-title">⚙ Settings</h2>

      {/* ════ Export ════ */}
      <div className="settings-card">
        <div className="settings-card-header">
          <span className="settings-card-icon">📤</span>
          <div>
            <h3 className="settings-card-title">Export Backup</h3>
            <p className="settings-card-desc">
              Downloads all your data as a single JSON file you can restore later.
            </p>
          </div>
        </div>

        <div className="export-summary">
          {summary.map(s => (
            <div key={s.label} className="export-summary-row">
              <span className="esr-label">{s.label}</span>
              <span className="esr-value">{s.value}</span>
            </div>
          ))}
          <div className="export-summary-row">
            <span className="esr-label">Includes</span>
            <span className="esr-value">positions · stops · watchlist · DCA inputs · review history</span>
          </div>
        </div>

        <button className="btn btn-primary settings-btn" onClick={handleExport}>
          📥 Download Backup
        </button>
      </div>

      {/* ════ Import ════ */}
      <div className="settings-card">
        <div className="settings-card-header">
          <span className="settings-card-icon">📥</span>
          <div>
            <h3 className="settings-card-title">Import Backup</h3>
            <p className="settings-card-desc">
              Restore from a previously exported <code>investment-backup-*.json</code> file.
            </p>
          </div>
        </div>

        {/* Warning banner */}
        <div className="import-warning">
          ⚠ Import will <strong>replace all current data</strong>. This cannot be undone.
          Export a backup first if you want to preserve your current state.
        </div>

        {/* File input (hidden) */}
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* States */}
        {importState === null && (
          <button className="btn btn-ghost settings-btn" onClick={() => fileRef.current?.click()}>
            📂 Choose Backup File
          </button>
        )}

        {importState === 'confirm' && pendingData && (
          <div className="import-confirm">
            <p className="import-confirm-text">
              Backup from <strong>{pendingData.exportedAt ? new Date(pendingData.exportedAt).toLocaleString() : 'unknown date'}</strong>
              {' '}containing {pendingData.positions?.length ?? 0} positions,{' '}
              {pendingData.fmStocks?.length ?? 0} watchlist stocks,{' '}
              {pendingData.reviews?.length ?? 0} reviews.
            </p>
            <p className="import-confirm-warn">Replace all current data with this backup?</p>
            <div className="import-confirm-btns">
              <button className="btn btn-ghost" onClick={handleImportCancel}>Cancel</button>
              <button className="btn btn-danger" onClick={handleImportConfirm}>Yes, Import Now</button>
            </div>
          </div>
        )}

        {importState === 'success' && (
          <div className="import-result import-result--ok">
            <p>✓ Import successful. Reload the app to see your restored data.</p>
            <button className="btn btn-primary settings-btn" onClick={() => window.location.reload()}>
              Reload App
            </button>
          </div>
        )}

        {importState?.error && (
          <div className="import-result import-result--error">
            <p>✕ Import failed: {importState.error}</p>
            <button className="btn btn-ghost settings-btn" onClick={() => { setImportState(null); fileRef.current?.click(); }}>
              Try Another File
            </button>
          </div>
        )}
      </div>

      {/* ════ Notifications ════ */}
      <div className="settings-card">
        <div className="settings-card-header">
          <span className="settings-card-icon">🔔</span>
          <div>
            <h3 className="settings-card-title">Notifications</h3>
            <p className="settings-card-desc">
              Portfolio alerts for stop-loss risk, allocation limits, and weekly review.
            </p>
          </div>
        </div>

        {/* Permission status row */}
        <div className="notif-permission-row">
          <div className="notif-perm-status">
            <span className={`notif-perm-dot notif-perm-dot--${permission}`} />
            <span className="notif-perm-label">
              {permission === 'granted'     ? 'Permission granted'
                : permission === 'denied'   ? 'Blocked — open browser Settings to allow'
                : permission === 'default'  ? 'Permission not yet requested'
                : 'Not supported on this browser'}
            </span>
          </div>
          {permission === 'default' && (
            <button
              className="btn btn-primary notif-perm-btn"
              onClick={requestPermission}
            >
              Allow
            </button>
          )}
        </div>

        {permission === 'denied' && (
          <div className="notif-denied-hint">
            On iPhone: Settings → Safari → {'{'}this site{'}'} → Notifications → Allow
          </div>
        )}

        {/* Master on/off toggle */}
        <div className="notif-master-row">
          <div className="notif-master-text">
            <span className="notif-master-label">Enable alert monitoring</span>
            <span className="notif-master-desc">
              Checks conditions every 60 s while the app is open
            </span>
          </div>
          <label className="notif-toggle" aria-label="Enable notifications">
            <input
              type="checkbox"
              checked={prefs.enabled}
              disabled={permission !== 'granted'}
              onChange={e => setPrefs({ ...prefs, enabled: e.target.checked })}
            />
            <span className="notif-toggle-slider" />
          </label>
        </div>

        {/* Per-type toggles — only when master is on */}
        {prefs.enabled && permission === 'granted' && (
          <div className="notif-types">
            {NOTIF_TYPES.map(nt => (
              <div key={nt.key} className="notif-type-row">
                <div className="notif-type-info">
                  <span className="notif-type-icon" aria-hidden="true">{nt.icon}</span>
                  <div>
                    <span className="notif-type-label">{nt.label}</span>
                    <span className="notif-type-desc">{nt.desc}</span>
                  </div>
                </div>
                <label className="notif-toggle" aria-label={`Toggle ${nt.label}`}>
                  <input
                    type="checkbox"
                    checked={prefs[nt.key] ?? true}
                    onChange={e => setPrefs({ ...prefs, [nt.key]: e.target.checked })}
                  />
                  <span className="notif-toggle-slider" />
                </label>
              </div>
            ))}
          </div>
        )}

        {/* iOS install tip */}
        <div className="notif-ios-tip">
          📱 <strong>iPhone:</strong> Install via Safari's <em>Share → Add to Home Screen</em> for full
          Web Push support (iOS 16.4 +). The Sunday reminder fires while the app is open.
        </div>
      </div>

      {/* ════ About ════ */}
      <div className="settings-card settings-card--about">
        <div className="settings-card-header">
          <span className="settings-card-icon">ℹ</span>
          <div>
            <h3 className="settings-card-title">About</h3>
          </div>
        </div>
        <div className="about-rows">
          <div className="about-row"><span>App</span><span>Investment Strategy App</span></div>
          <div className="about-row"><span>Storage</span><span>Browser localStorage (no server)</span></div>
          <div className="about-row"><span>Data keys</span><span>isa_positions · isa_fm_stocks · isa_reviews · …</span></div>
        </div>
      </div>
    </div>
  );
}
